import dayjs, { Dayjs, PluginFunc } from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isBetween from 'dayjs/plugin/isBetween';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import weekYear from 'dayjs/plugin/weekYear';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import localizedFormat from 'dayjs/plugin/localizedFormat';
import pluralGetSet from 'dayjs/plugin/pluralGetSet';
import updateLocale from 'dayjs/plugin/updateLocale';
import minMax from 'dayjs/plugin/minMax';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import utc from 'dayjs/plugin/utc';
import dayOfYear from 'dayjs/plugin/dayOfYear';
import { importLibLocale, t } from 'src/i18n/i18next';
import advancedFormat from 'dayjs/plugin/advancedFormat'

export const WEEK_FORMAT = 'GGGG-WW';
export const WEEK_FORMAT_SHORT = 'GGGG-W';

const locales = import.meta.glob('/node_modules/dayjs/esm/locale/*.js');

if (CONFIG.debug) {
  window.dayjs = dayjs;
}

const shortISOFormat: PluginFunc = (o, c) => {
  const proto = c.prototype;
  const oldFormat = proto.format;
  proto.toShortISOString = function () {
    return oldFormat.call(this, 'YYYY-MM-DD');
  };
};

declare module 'dayjs' {
  interface Dayjs {
    // ISO8601/RFC3339 (`YYYY-MM-DD`)
    toShortISOString(): string,

    parse({ date, utc, args }: { date: dayjs.Dayjs, utc: boolean, args: [string, string] }): void
  }
}

type DayJSInternal = Dayjs & {
  $d: Dayjs,
  $L: string,
  init: () => void
}

const preParsePlugin: PluginFunc = (o, c, d) => {
  const proto = c.prototype;
  const oldParse = proto.parse;
  proto.parse = function (cfg) {
    const [dateStr, format] = cfg.args;
    if (!format) {
      return oldParse.call(this, cfg);
    }

    if (typeof format === 'string' && format.includes('G-W') && format !== WEEK_FORMAT && format !== WEEK_FORMAT_SHORT) {
      throw new Error(`${WEEK_FORMAT}/${WEEK_FORMAT_SHORT} format for weeks supported only`);
    }

    if (format === WEEK_FORMAT || format === WEEK_FORMAT_SHORT) {
      const [year, week] = dateStr.split('-');
      const result = d.call(this).year(+year).week(+week);
      if (year && +year > 1950 && week && week !== '0' && result.isValid()) {
        const startOfWeek = result.startOf('week') as DayJSInternal;
        (this as DayJSInternal).$d = startOfWeek.$d;
        (this as DayJSInternal).$L = startOfWeek.$L;
        return (this as DayJSInternal).init();
      }
    }

    return oldParse.call(this, cfg);
  };
};

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(isoWeek);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isBetween);
dayjs.extend(updateLocale);
dayjs.extend(localizedFormat);
dayjs.extend(shortISOFormat);
dayjs.extend(pluralGetSet);
dayjs.extend(customParseFormat);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);
dayjs.extend(minMax);
dayjs.extend(preParsePlugin);
dayjs.extend(advancedFormat);
dayjs.extend(dayOfYear);

export const setDayJsLocaleAndFormat = async (language: string, dateFormat: string) => {
  const localeData = await importLibLocale(locales, language === 'en' ? 'en-gb' : language);
  if (!localeData) {
    return;
  }
  dayjs.locale(language, localeData.default);
  dayjs.updateLocale(language, {
    weekStart: +t('backbone.tuiCustomSettings.dates.week.dow'),
    yearStart: +t('backbone.tuiCustomSettings.dates.week.day'),
    formats: {
      LT: t('backbone.tuiCustomSettings.dates.longDateFormat.LT'),
      L: dateFormat,
      LL: t('backbone.tuiCustomSettings.dates.longDateFormat.LL'),
      LLL: t('backbone.tuiCustomSettings.dates.longDateFormat.LLL'),
      LLLL: t('backbone.tuiCustomSettings.dates.longDateFormat.LLLL')
    }
  });
};
